import Button from '@asphalt-react/button';
import Flag from '@asphalt-react/flag';
import { Search } from '@asphalt-react/textfield';
import cn from 'classnames';
import firebase from 'firebase/app';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { Image, Shimmer } from 'react-shimmer';
import VariantTabControl from '../components/VariantTabControl';
import Permission from '../../authorization/components/Permission';
import actions from '../../authorization/models/actions';
import ConfigProvider from '../../common/components/ConfigProvider';
import configKeys from '../../common/models/config-keys';
import routes from '../../common/models/route-constants';
import NoSearchResultsFound from '../../common/components/NoSearchResultsFound';
import styles from './IllustrationHomePage.module.css';

function IllustrationCard({ doc, path }) {
    const [selectedTab, setSelectedTab] = useState('light');
    const data = doc.data();
    const hasDarkVariant = data.variant_sync === false;
    const isDark = hasDarkVariant && selectedTab === 'dark';
    const lightImgUrl = data.default.img_url;
    const darkImgUrl = data.default.img_url_dark ?? lightImgUrl;
    const imgUrl = isDark ? darkImgUrl : lightImgUrl;

    const imageView = 'loading' in HTMLImageElement.prototype ? (
        <img
            key={selectedTab}
            loading='lazy'
            src={imgUrl}
            className={styles.image}
            alt={data.combined_name}
        />
    ) : (
        <Image
            key={selectedTab}
            src={imgUrl}
            className={styles.image}
            fallback={<Shimmer width={360} height={240} />}
            NativeImgProps={{ alt: data.combined_name }}
        />
    );

    return (
        <Link to={`${path}/${doc.id}`} className={styles.illustrationCard}>
            <p className={`${styles.illustrationName} ${hasDarkVariant ? styles.illustrationNameCompact : ''}`}>{data.combined_name}</p>
            {hasDarkVariant && (
                <VariantTabControl
                    selectedTab={selectedTab}
                    onTabChange={setSelectedTab}
                    className={styles.cardTabContainer}
                />
            )}
            <div className={`${styles.imageContainer} ${isDark ? styles.imageContainerDark : ''} ${hasDarkVariant ? styles.imageContainerCompact : ''}`}>
                {imageView}
            </div>
        </Link>
    );
}

function IllustrationHomePage() {
    let { path } = useRouteMatch();
    let history = useHistory();
    const location = useLocation();
    const [illustrations, setIllustrations] = useState([]);
    const [notification, setNotification] = useState(null);
    const [filteredIllustrations, setFilteredIllustrations] = useState([]);
    const [filterString, setFilterString] = useState('');

    const clearHistoryNotificationState = (history) => {
        let state = { ...history.location.state };
        delete state.notification;
        history.replace({
            ...history.location,
            state,
        });
    };

    const filter = _.debounce((term) => {
        setFilterString(term);
        filterList(term);
    }, 300);

    const filterList = (inputFilterString) => {
        if (_.isEmpty(inputFilterString)) {
            setFilteredIllustrations(illustrations);
            return;
        }
        // constructing the regex string for case-insensitive matching
        var regex = new RegExp(_.escapeRegExp(inputFilterString), 'i');

        setFilteredIllustrations(
            _.filter(illustrations, (illustration) => {
                return illustration.data().combined_name.match(regex);
            })
        );
    };

    useEffect(() => {
        if (location.state && location['state'].notification) {
            setNotification(location.state.notification);
        }

        // set listener
        var db = firebase.firestore();
        const unsubscribeGetAllIllustrationsListener = db
            .collection('illustrations')
            .orderBy('default.created_at', 'desc')
            .onSnapshot(
                function (snapshot) {
                    setIllustrations(snapshot.docs);
                    if (_.isEmpty(filterString)) {
                        setFilteredIllustrations(snapshot.docs);
                    }
                },
                function (error) {
                    throw error;
                }
            );
        return () => {
            unsubscribeGetAllIllustrationsListener();
        };
    }, [history, location, filterString]);

    return (
        <div style={{ width: '100%' }}>
            <div className={cn('panel', { show: notification })}>
                <div className={cn('animateFlag', { visible: notification })}>
                    <Flag
                        success
                        floating
                        title={notification ? notification.title : ''}
                        content={notification ? notification.content : ''}
                        actionable
                        engageCaption={notification ? notification.ctaText : ''}
                        engageProps={{
                            onClick: () => {
                                if (notification) {
                                    setNotification(null);
                                    clearHistoryNotificationState(history);
                                    history.push(
                                        `${path}/${notification.illustrationId}`
                                    );
                                }
                            },
                        }}
                        onDismiss={() => {
                            setNotification(null);
                            clearHistoryNotificationState(history);
                        }}
                    />
                </div>
            </div>
            <div className={styles.homePageContainer}>
                <div className={cn(styles.topBarContainer, 'shadowLow')}>
                    <div className={styles.topBar}>
                        <div className={styles.headerContainer}>
                            <h3 className={styles.heading}>
                                Default Illustrations
                            </h3>
                            <div className={styles.buttonContainer}>
                                <ConfigProvider
                                    configKey={
                                        configKeys.IS_CREATE_DEFAULT_ILLUSTRATION_ENABLED
                                    }>
                                    <Permission
                                        action={actions.CREATE_ILLUSTRATION}>
                                        <Button
                                            onClick={() => {
                                                history.push(
                                                    `${path}/${routes.CREATE}`
                                                );
                                            }}>
                                            Add default illustration
                                        </Button>
                                    </Permission>
                                </ConfigProvider>
                            </div>
                        </div>
                        <Search
                            onChange={(e) => {
                                filter(e.target.value);
                            }}
                            size='m'
                            placeholder='Search for illustrations by key or name'
                            stretch
                        />
                    </div>
                </div>
                <div className={styles.gridContainer}>
                    {filteredIllustrations.length > 0 ? (
                        <div className={styles.grid}>
                            {filteredIllustrations.map((doc) => (
                                <IllustrationCard
                                    key={doc.data().combined_name}
                                    doc={doc}
                                    path={path}
                                />
                            ))}
                        </div>
                    ) : !_.isEmpty(filterString) ? (
                        <NoSearchResultsFound searchQuery={filterString} />
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export default IllustrationHomePage;
